import { useCallback, useMemo } from 'react';

import {
  useErpGroupSkillsQuery,
  useSaveGroupSkillsMutation,
} from 'core/graphql';

import { IGroupProgramLesson, IGroupSkillRow } from './careerModel';

const EMPTY_ROWS: IGroupSkillRow[] = [];
const EMPTY_LESSONS: IGroupProgramLesson[] = [];

/**
 * ТЗ 0041 — набор навыков конкретной ГРУППЫ и её собственные занятия.
 *
 * Один запрос на обе половины: строки набора без списка занятий не
 * отредактировать (выбирать не из чего), а список занятий сам по себе экрану
 * не нужен. Два запроса дали бы мигание «занятий нет» на первой отрисовке.
 *
 * `skip` без `groupId` — вкладка монтируется вместе с карточкой, а id приходит
 * из маршрута.
 */
export const useGroupSkills = (groupId: string | null) => {
  const { data, loading, refetch } = useErpGroupSkillsQuery({
    variables: { groupId: groupId as string },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
  });

  // Раскладываем явно: сервер отдаёт `Maybe<...>` (null ИЛИ undefined), а
  // экран различает «занятия нет» и «занятие есть, но ещё не прошло».
  const rows: IGroupSkillRow[] = useMemo(
    () =>
      (data?.careerGroupSkills ?? []).map((row) => ({
        id: row.id,
        skillId: row.skillId,
        skillName: row.skillName,
        skillCategory: row.skillCategory,
        level: row.level,
        unlockLessonId: row.unlockLessonId ?? null,
        unlockLessonNumber: row.unlockLessonNumber ?? null,
        unlockLessonName: row.unlockLessonName ?? null,
        unlockLessonDate: row.unlockLessonDate ?? null,
        order: row.order,
        isActive: row.isActive,
      })),
    [data],
  );

  const lessons: IGroupProgramLesson[] = useMemo(
    () =>
      (data?.groupProgramLessons ?? []).map((lesson) => ({
        lessonId: lesson.lessonId,
        number: lesson.number,
        name: lesson.name,
        moduleName: lesson.moduleName,
        date: lesson.date ?? null,
        isOver: lesson.isOver,
      })),
    [data],
  );

  return {
    rows: rows.length ? rows : EMPTY_ROWS,
    lessons: lessons.length ? lessons : EMPTY_LESSONS,
    loading,
    refetch,
  };
};

/**
 * ТЗ 0041 — сохранение набора навыков группы.
 *
 * Набор пишется ЦЕЛИКОМ, как и у курса: строка, которой нет во входе,
 * деактивируется. Два разных представления об одном экране («сохранить набор»
 * против «отцепить строку») здесь осознанно не смешивают.
 */
export const useSetGroupSkills = () => {
  const [save, { loading }] = useSaveGroupSkillsMutation();

  const setSkills = useCallback(
    async (
      groupId: string,
      rows: {
        skillId: string;
        level: number;
        unlockLessonId: string | null;
      }[],
    ) => {
      const { data } = await save({
        variables: {
          groupId,
          rows: rows.map((row, index) => ({
            skillId: row.skillId,
            level: row.level,
            // Шлём `null` ЯВНО: набор пишется целиком, и пропущенное поле
            // сервер прочитал бы как «не менять», а значит связка осталась бы
            // висеть на прежнем занятии.
            unlockLessonId: row.unlockLessonId,
            order: index,
          })),
        },
      });
      const payload = data?.setGroupSkills;
      return {
        success: Boolean(payload?.success),
        added: payload?.added ?? 0,
        updated: payload?.updated ?? 0,
        deactivated: payload?.deactivated ?? 0,
        errors: (payload?.customErrors ?? []).map((item) => ({
          field: item?.field ?? '',
          message: item?.message ?? '',
        })),
      };
    },
    [save],
  );

  return { setSkills, saving: loading };
};
